import type { OTCDealData } from '@src/types/OTC';
import {
  Box,
  DealStatusEnum,
  FormattedMessage,
  HStack,
  Icon,
  IconName,
  Stack,
  Text,
  abbreviateId,
  formattedDay,
  formattedTime,
  useSecuritiesContext,
} from '@talos/kyoko';
import { memo } from 'react';
import { defineMessages } from 'react-intl';

interface DealCardHeaderProps {
  deal: OTCDealData;
}

const messages = defineMessages({
  id: {
    id: 'OMS.ManualDealTradeView.id',
    defaultMessage: 'ID',
  },
});

export const DealCardHeader = memo(function DealCardHeader({ deal }: DealCardHeaderProps) {
  const { securitiesBySymbol } = useSecuritiesContext();

  const symbol = deal.Symbol;
  const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
  const statusDisplayDetails = getStatusDisplayDetails(deal.Status);

  return (
    <Stack gap="spacing2">
      <HStack gap="spacing2" justifyContent="space-between">
        <Text weight="fontWeightMedium" size="fontSizeSm">
          {symbol ? (security?.DisplaySymbol ?? symbol) : '-'}
        </Text>
        <HStack gap="spacing2" alignItems="center">
          <Icon icon={statusDisplayDetails.icon} color={statusDisplayDetails.color} size="fontSizeSmall" />
          <Text size="fontSizeSmall" color="colorTextSecondary">
            {deal.Status}
          </Text>
        </HStack>
      </HStack>
      <HStack gap="spacing2" alignItems="flex-end" justifyContent="space-between">
        <Text size="fontSizeSmall" color="colorTextSecondary">
          {formattedDay(deal.Timestamp)}
        </Text>
        <Text size="fontSizeSmall" color="colorTextSecondary">
          {formattedTime(deal.Timestamp)}
        </Text>

        <Text size="fontSizeSmall" color="colorTextSubtle">
          <label>
            <FormattedMessage {...messages.id} />{' '}
          </label>
          <Box as="span" color="colors.gray.100" title={deal.ID}>
            {abbreviateId(deal.ID)}
          </Box>
        </Text>
      </HStack>
    </Stack>
  );
});

function getStatusDisplayDetails(status: DealStatusEnum) {
  switch (status) {
    case DealStatusEnum.Pending:
      return {
        icon: IconName.Circle,
        color: 'colorTextSecondary',
      };
    case DealStatusEnum.Live:
      return {
        icon: IconName.CheckCircleSolid,
        color: 'colorSuccess',
      };
    default:
      return {
        icon: IconName.Circle,
        color: 'colorTextSecondary',
      };
  }
}
